import React from "react";
import { Row, Col, Button, ListGroup, Alert } from "react-bootstrap";
import Moment from "moment";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import CreatableSelect from "react-select/creatable";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";

function AddressList({
  data,
  isLoading,
  isAuthenticated = true,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  filter,
  user,
  validateRelatedRecord,
  streetOptions = [],
}) {
  const dataSet = "addresses";
  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const [showAlert, setShowAlert] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const tableMeta = {
    updateData: (rowIndex, columnId, value) => {
      updateTableState(dataSet, rowIndex, columnId, value);
    },
    handleSave: async ({ row, column, value }) => {
      const record = {
        ...(tableData[row.index] || {}),
        ...row.original,
        ...(column?.id ? { [column.id]: value } : {}),
      };

      const validation = validateRelatedRecord?.(record, dataSet) || [true, null];
      if (validation[0] !== true) {
        setErrorMessage(validation[1] || "Validation failed");
        setShowAlert(true);
        return { validationFailed: true };
      }

      setShowAlert(false);
      setErrorMessage("");

      try {
        await saveRecord(record);
      } catch (error) {
        console.error("Address save failed:", error);
        return { saveFailed: true };
      }

      return true;
    },
    handleDelete: async ({ row }) => {
      const confirmed = window.confirm("Are you sure you want to delete this record?");
      if (!confirmed) {
        return;
      }

      await deleteRecord(dataSet, row.original);

      const deletedId = row.original.street_address_id;
      const deletedClientId = row.original._client_id;
      const nextRows = tableData.filter((item) => {
        const matchesSavedId =
          deletedId !== null &&
          deletedId !== undefined &&
          String(item.street_address_id) === String(deletedId);
        const matchesClientId =
          deletedClientId !== null &&
          deletedClientId !== undefined &&
          item._client_id === deletedClientId;

        return !(matchesSavedId || matchesClientId);
      });

      updateTableState(nextRows, dataSet);
    },
  };

  const renderStreetNameCell = ({ row, getValue }) => {
    const value = getValue();
    const selectedOption = value
      ? streetOptions.find((option) => option.value === value) || { value, label: value }
      : null;

    const saveStreetName = async (nextStreetName) => {
      await tableMeta.handleSave({
        row,
        column: { id: "street_name" },
        value: nextStreetName,
      });
    };

    return (
      <CreatableSelect
        inputId={`address-list-street-name-${row.original.street_address_id ?? row.index}`}
        classNamePrefix="react-select"
        value={selectedOption}
        options={streetOptions}
        onCreateOption={async (inputValue) => {
          const trimmedValue = String(inputValue || "").trim();
          if (!trimmedValue) {
            return;
          }

          const nextOptions = [...streetOptions, { value: trimmedValue, label: trimmedValue }].filter(
            (option, index, options) => options.findIndex((candidate) => candidate.value === option.value) === index
          );

          updateTableState(nextOptions, "streetOptions");
          updateTableState(dataSet, row.index, "street_name", trimmedValue);
          await saveStreetName(trimmedValue);
        }}
        onChange={async (nextOption) => {
          const nextStreetName = nextOption?.value || "";
          updateTableState(dataSet, row.index, "street_name", nextStreetName);
          await saveStreetName(nextStreetName);
        }}
        onBlur={async () => {
          await tableMeta.handleSave({
            row,
            column: { id: "street_name" },
            value: tableData[row.index]?.street_name ?? selectedOption?.value ?? "",
          });
        }}
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base) => ({
            ...base,
            minHeight: "31px",
            height: "31px",
            fontSize: "12px",
            border: "none",
            boxShadow: "none",
            backgroundColor: "transparent",
          }),
          valueContainer: (base) => ({ ...base, padding: "0 6px" }),
          indicatorsContainer: (base) => ({ ...base, height: "31px" }),
        }}
      />
    );
  };

  const columns = [
    columnHelper.accessor("street_address_id", {
      id: "street_address_id",
      header: "ID",
      size: 60,
    }),
    columnHelper.accessor("street_num_low", {
      id: "street_num_low",
      header: "St No Low",
      cell: EditableCell,
      size: 85,
      meta: { numericOnly: true, inputType: "number" },
    }),
    columnHelper.accessor("street_num_low_suffix", {
      id: "street_num_low_suffix",
      header: "St No Low Suffix",
      cell: EditableCell,
      size: 110,
    }),
    columnHelper.accessor("street_num_high", {
      id: "street_num_high",
      header: "St No High",
      cell: EditableCell,
      size: 85,
      meta: { numericOnly: true, inputType: "number" },
    }),
    columnHelper.accessor("street_num_high_suffix", {
      id: "street_num_high_suffix",
      header: "St No High Suffix",
      cell: EditableCell,
      size: 110,
    }),
    columnHelper.accessor("known_as", {
      id: "known_as",
      header: "Known As",
      cell: EditableCell,
      size: 120,
    }),
    columnHelper.accessor("street_name", {
      id: "street_name",
      header: "Street Name",
      cell: renderStreetNameCell,
      size: 160,
    }),
    columnHelper.accessor("frontage", {
      id: "frontage",
      header: "Frontage",
      cell: EditableCell,
      size: 85,
    }),
    columnHelper.accessor("sort_order", {
      id: "sort_order",
      header: "Sort Order",
      cell: EditableCell,
      size: 85,
      meta: { numericOnly: true },
    }),
    columnHelper.accessor("main_address", {
      id: "main_address",
      header: "Main Address",
      cell: EditableCell,
      size: 95,
      meta: { type: "checkbox" },
    }),
    columnHelper.accessor("last_edited_date", {
      id: "last_edited_date",
      header: "Last Edited",
      cell: (info) => {
        const value = info.getValue();
        return value ? Moment(value).local().format("DD-MM-YYYY") : null;
      },
      size: 95,
    }),
    columnHelper.accessor("last_edited_by", {
      id: "last_edited_by",
      header: "Edited By",
      size: 100,
    }),
    columnHelper.display({
      id: "delete_address",
      header: "",
      cell: ({ row }) => (
        <Button
          id={`address-list-delete-${row.original.street_address_id ?? row.index}`}
          type="button"
          size="sm"
          variant="danger"
          onClick={async (event) => {
            event.preventDefault();
            await tableMeta.handleDelete({ row });
          }}
        >
          Delete
        </Button>
      ),
      size: 80,
      enableSorting: false,
    }),
  ];

  function addRecord() {
    const nextRows = [
      {
        _client_id: `address-${Date.now()}-${tableData.length}`,
        property_id: filter?.propertyid,
        property_key: null,
        street_name: "",
        main_address: false,
        odd_even: false,
        sort_order: tableData.length + 1,
        street_num_low: null,
        street_num_low_suffix: null,
        street_num_high: null,
        street_num_high_suffix: null,
        frontage: null,
        known_as: null,
        rp_data_id: null,
        auto_match: false,
        manual_match: false,
        created_by: user,
        last_edited_by: user,
        archived_date: null,
        new_record: true,
      },
      ...tableData,
    ];

    updateTableState(nextRows, dataSet);
  }

  function renderList(rows) {
    if (!rows) {
      return null;
    }

    return [{}].concat(rows).map((address, index) =>
      index !== 0 ? (
        <ListGroup.Item key={address.street_address_id || address._client_id || index}>
          {(address.sort_order || index).toString() + ") "}
          {(address.street_num_low || "") + (address.street_num_low_suffix || "")}
          {address.street_num_high && address.street_num_high !== address.street_num_low
            ? ` - ${address.street_num_high || ""}${address.street_num_high_suffix || ""}`
            : ""}
          {(address.known_as || "").length > 0 ? ` (${address.known_as})` : ""} {address.street_name}
          {address.main_address ? " (main address)" : ""}
        </ListGroup.Item>
      ) : (
        ""
      )
    );
  }

  function renderComponent() {
    return (
      <div className="addresses">
        {showAlert && <Alert variant="danger">{errorMessage}</Alert>}
        {controlType === "table" && (
          <Row className="show-Container">
            <Col className="text-right">
              <Button id="address-list-add-button" variant="secondary" size="sm" onClick={addRecord}>
                Add
              </Button>
            </Col>
          </Row>
        )}

        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color="#e03c31"
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : controlType === "table" ? (
              <GenericTable
                columns={columns}
                data={tableData}
                section="address"
                pageSize={50}
                meta={tableMeta}
              />
            ) : (
              renderList(tableData)
            )}
          </Col>
        </Row>
      </div>
    );
  }

  return <div className="AddressSection">{isAuthenticated ? renderComponent() : null}</div>;
}

export default AddressList;